<template>
  <ul class="package-content">
    <li v-for="(row, index) in memberRows" :key="row.member.AccessoryId">
      <button
        type="button"
        :title="i18n.t('/AccessoryCatalog/PackageMemberTooltip')"
        :data-qa="`accessory-detail-package-member-${row.member.AccessoryId}`"
        @click="$emit('open', index)"
      >
        <span class="t">
          {{ row.title }}
          <code>{{ row.member.AccessoryId }}</code>
        </span>
        <span class="c">{{ row.member.Count }}&times;</span>
        <span class="p">
          {{ i18n.n(row.price, 'currency') }}
          <small v-if="row.struck !== null">{{ i18n.n(row.struck, 'currency') }}</small>
        </span>
        <icon icon="chevron-right" aria-hidden="true" />
      </button>
    </li>
  </ul>
</template>

<script>
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import { VAT_INCL, packageMemberAsAccessory } from '@/helpers/ponCatalog'

export default {
  components: {
    Icon,
  },
  props: {
    /** Package members as `packageContent()` returns them */
    members: {
      type: Array,
      required: true,
    },
    vat: {
      type: String,
      default: VAT_INCL,
    },
    /** True while the package itself is sold at its action price */
    actionActive: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
    }
  },
  computed: {
    /**
     * Everything a row draws, resolved once per member instead of per render:
     * as template methods these rebuilt an accessory-shaped object for every
     * member on every unrelated re-render of the panel around them.
     *
     * `price` is what one piece of the member costs inside the package — the
     * action variant when the package itself is running its action. Per piece,
     * like every other price in the panel; the catalog bills the line for all
     * `Count` of them. `struck` is the regular price, shown struck through, but
     * only when the action actually differs from it.
     */
    memberRows() {
      return this.members.map((member) => {
        const price = packageMemberAsAccessory(member, this.actionActive).Price[this.vat]
        const regular = this.actionActive
          ? packageMemberAsAccessory(member, false).Price[this.vat]
          : price
        return {
          member,
          title: member.Catalog?.Title || member.AccessoryId,
          price,
          struck: regular === null || regular === price ? null : regular,
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// No global font-size scale exists in styles/variables, so the two sizes this
// list needs are named here rather than repeated as fractional literals.
$row-size: 14px;
$meta-size: 12px;

.package-content {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid $gray-light;
  border-radius: 10px;
  overflow: hidden;

  li + li {
    border-top: 1px solid $gray-lightest;
  }

  button {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 2.5em minmax(0, 6em) 14px;
    gap: 10px;
    align-items: center;
    width: 100%;
    padding: 9px 14px;
    border: 0;
    background: transparent;
    font-size: $row-size;
    text-align: left;
    cursor: pointer;
    transition: background 0.14s ease;

    &:hover {
      background: $gray-lightest;
    }
  }

  .t {
    min-width: 0;
    color: $text-color;
    line-height: 1.35;

    // The article number sits under the description; side by side they crowd
    code {
      display: block;
      margin-top: 2px;
      font-family: $font-family-mono;
      font-size: $meta-size;
      color: $gray-dark;
    }
  }

  .c {
    text-align: right;
    color: $gray-dark;
    font-variant-numeric: tabular-nums;
  }

  .p {
    text-align: right;
    font-weight: 700;
    font-variant-numeric: tabular-nums;

    small {
      display: block;
      font-size: $meta-size;
      font-weight: 400;
      color: $gray-dark;
      text-decoration: line-through;
    }
  }

  svg {
    @include icon;
    width: 14px;
    height: 14px;
    color: $gray-dark;
  }
}
</style>
